import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { guestNameSchema } from "@wedding/shared";
import { useAuth } from "../contexts/auth-context";
import { useTurnstile } from "../hooks/use-turnstile";
import { createApi } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Canlı demo düğününde telefon formunun YERİNE: "Demoya katıl" (F7).
 *
 * Yalnız `wedding.config.demo` iken render edilir; bu bir UI ipucudur — yetki
 * sunucuda (`POST /auth/demo`, worker lib/demo.ts isDemoWedding). Başarıda
 * oturum açılır ve sayfa kendi yönlendirmesini yapar (kayıt akışıyla aynı).
 * Paylaşılanlar moderasyona düşer ve her gece silinir — kullanıcıya söylenir.
 */
export interface DemoEnterClasses {
  card: string;
  label: string;
  input: string;
  button: string;
  muted: string;
  error: string;
  heading: string;
}

export function DemoEnter({ slug, classes: s, style }: { slug: string; classes: DemoEnterClasses; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const { markAuthenticated } = useAuth();
  const { containerRef, token, error: turnstileError, reset: resetTurnstile } = useTurnstile();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const nameOk = name.trim() === "" || guestNameSchema.safeParse(name).success;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || loading || !nameOk) return;
    setLoading(true);
    setError("");
    try {
      const res = (await createApi(slug).post<unknown>("/auth/demo", {
        turnstileToken: token,
        ...(name.trim() ? { name: guestNameSchema.parse(name) } : {}),
      })) as unknown as { ok: boolean; authed?: boolean; guest?: { name: string | null } };
      if (res.authed) {
        markAuthenticated({ name: res.guest?.name ?? null, phone: "" });
        return;
      }
      setError(t("demoEnter.failed"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(msg.includes("cap_reached") || msg.includes("429") ? t("demoEnter.capReached") : t("demoEnter.failed"));
    } finally {
      setLoading(false);
      resetTurnstile();
    }
  }

  return (
    <form onSubmit={submit} className={s.card} style={style} data-testid="demo-enter">
      <p className={cn("flex items-center gap-2", s.heading)}>
        <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t("demoEnter.title")}
      </p>
      <p className={cn("mt-2 text-sm leading-relaxed", s.muted)}>{t("demoEnter.body")}</p>

      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="demo-name" className={s.label}>{t("demoEnter.nameLabel")}</label>
          <input
            id="demo-name"
            type="text"
            autoComplete="given-name"
            placeholder={t("demoEnter.namePlaceholder")}
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={!nameOk}
            className={s.input}
          />
          {!nameOk && <p role="alert" className={cn("mt-1.5 text-xs", s.error)}>{t("join.invalidName")}</p>}
        </div>

        {error && <p role="alert" className={cn("text-sm", s.error)}>{error}</p>}
        <div ref={containerRef} className="flex justify-center" />
        {turnstileError && <p role="alert" className={cn("text-sm", s.error)}>{turnstileError}</p>}

        <button type="submit" disabled={!token || loading || !nameOk} className={cn(s.button, "flex cursor-pointer items-center justify-center gap-2")}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {loading ? t("demoEnter.entering") : t("demoEnter.submit")}
        </button>
        <p className={cn("text-xs leading-relaxed", s.muted)}>{t("demoEnter.note")}</p>
      </div>
    </form>
  );
}
